import React, { useState } from 'react';
import { Heart, ShoppingCart, Zap } from 'lucide-react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { addToCart, addToWishlist } from '../../../store/slice/userActions';
import productImg from '../../../assets/product.jpg'

import { getFileUrl } from '../../../utils/helper';
import { FaMinus, FaPlus } from 'react-icons/fa';

const ProductCard = ({ product }) => {
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [quantity, setQuantity] = useState(1);

    const dispatch = useDispatch();

    const generateDummyImage = (name) => {
        return productImg;
    };

    const increaseQuantity = () => setQuantity(prev => prev + 1);

    const decreaseQuantity = () => {
        if (quantity > 1) setQuantity(prev => prev - 1);
    };

    const handleAddToCart = async (e) => {
        e.stopPropagation();
        try {
            setIsAdding(true);
            await dispatch(addToCart({ productId: product?._id, quantity: quantity, type: "I" })).unwrap();
            toast.success(`Added ${quantity} item(s) to cart`);
            setQuantity(1);
        } catch (err) {
            toast.error(String(err) || "Failed to add to cart");
        } finally {
            setIsAdding(false);
        }
    };

    const handleWishlist = async (e) => {
        e.stopPropagation();
        try {
            setIsAdding(true);
            await dispatch(addToWishlist({ productId: product?._id })).unwrap();
            toast.success("Added to wishlist");
            setIsWishlisted(true);
        } catch (err) {
            toast.error(String(err) || "Failed to add to wishlist");
        } finally {
            setIsAdding(false);
        }
    };

    const imgSrc = product?.image ? getFileUrl(product?.image) : generateDummyImage(product?.name);
    const discount = product?.vendorPrice && product?.finalPrice
        ? Math.round(((product?.vendorPrice - product?.finalPrice) / product?.vendorPrice) * 100)
        : 0;

    const [showMore, setShowMore] = useState(false);

    const description = product?.description || "";
    const shortDescription = description.substring(0, 50);

    return (
        <div className="bg-white border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200">
            <div className="p-4">
                <div className="flex gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                            <div className={`flex-shrink-0 w-5 h-5 border-2 flex items-center justify-center mt-0.5 ${product?.food_type === 'VEG' ? 'border-green-600' : 'border-red-600'
                                }`}>
                                <div className={`w-2.5 h-2.5 rounded-full ${product?.food_type === 'VEG' ? 'bg-green-600' : 'bg-red-600'
                                    }`}></div>
                            </div>

                            <h3 className="text-[16px] font-bold text-gray-900 leading-tight flex-1">
                                {product?.name || 'Chicken Sausage Cheese Burst'}
                            </h3>
                        </div>

                        <div className="flex items-center gap-2 mb-3 ml-7 flex-wrap">
                            <span className="text-lg font-bold text-gray-900">
                                ₹{product?.finalPrice || 288}  <span className={`mx-2 capitalize${product?.food_type === 'Veg' ? 'bg-green-200' : 'bg-red-200'
                                    }`}>{product?.food_type}</span>
                            </span>
                            {discount > 0 && (
                                <span className="bg-green-50 text-green-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                                    <span className="text-green-600">●</span>
                                    ₹{discount} OFF USE WELCOMEDOM
                                </span>
                            )}
                        </div>

                        <p className="text-sm text-gray-600 leading-relaxed ml-7">
                            {showMore ? description : shortDescription}
                            {description.length > 50 && (
                                <button
                                    onClick={() => setShowMore(!showMore)}
                                    className="text-orange-600 font-semibold ml-1 hover:underline"
                                >
                                    {showMore ? "Show Less" : "Read More"}
                                </button>
                            )}
                        </p>
                    </div>

                    <div className="flex-shrink-0 w-32 sm:w-36">
                        <div className="relative">
                            <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-xl overflow-hidden relative shadow-md">
                                <img
                                    src={imgSrc}
                                    alt={product?.name}
                                    className="w-full h-full object-cover"
                                />

                                <button
                                    onClick={handleWishlist}
                                    className="absolute top-2 right-2 bg-white p-1.5 rounded-full shadow-md hover:shadow-lg transition-all hover:scale-110"
                                >
                                    <Heart
                                        className={`w-4 h-4 ${isWishlisted
                                            ? 'fill-red-500 text-red-500'
                                            : 'text-gray-600'
                                            }`}
                                    />
                                </button>

                                {product?.quantity === 0 && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                        <span className="text-white font-bold text-sm px-3 py-1 bg-red-500 rounded">
                                            Out of Stock
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-center bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        decreaseQuantity();
                                    }}
                                    disabled={quantity <= 1}
                                    className={`p-2 rounded-lg transition-all ${quantity <= 1
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-orange-600 hover:bg-orange-200 active:scale-95'
                                        }`}
                                >
                                    <FaMinus size={12} />
                                </button>

                                <span className="w-12 text-center font-bold text-gray-900 text-base">
                                    {quantity}
                                </span>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        increaseQuantity();
                                    }}
                                    className="p-2 text-orange-600 hover:bg-orange-200 rounded-lg transition-all active:scale-95"
                                >
                                    <FaPlus size={12} />
                                </button>
                                <button
                                    onClick={handleAddToCart}
                                    disabled={isAdding || product?.quantity === 0}
                                    className={`w-full px-1 py-2 rounded-full font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${product?.quantity === 0
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 active:scale-95 shadow-md hover:shadow-lg'
                                        }`}
                                >
                                    {isAdding ? (
                                        <div className="w-4 h-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <ShoppingCart size={12} />

                                        </>
                                    )}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;